import { useCallback, useEffect, useRef, useState } from 'react';

import { useApiClient } from '../providers/auth-provider';

import type { SandboxRuntimeResponse } from '../lib/api-client';

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Sandbox Runtime Logs list ViewModel: server-paged session history
 * (GET /sandbox_runtimes) with debounced server-side search, cursor
 * "load more", and a liveness poll. The poll only refreshes while the
 * user is on the FIRST page — once they page into deeper history they are
 * reading, not monitoring, and a background replace would yank rows out
 * from under them. Access-log hook discipline: status union, in-flight
 * guard, hidden-tab pause.
 */
export function useSandboxRuntimes({
  enabled = true,
  pollInterval = 15_000,
}: { enabled?: boolean; pollInterval?: number } = {}) {
  const apiClient = useApiClient();
  const [rows, setRows] = useState<SandboxRuntimeResponse[]>([]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | undefined>();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const nextCursorRef = useRef<string | null>(null);
  const pagesLoadedRef = useRef(0);
  const inFlight = useRef(false);
  const loadedOnce = useRef(false);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [search]);

  const fetchFirstPage = useCallback(
    (query: string) => {
      if (inFlight.current) return;
      inFlight.current = true;
      apiClient
        // scope=subtree: the operator view — sessions of the effective tenant
        // AND every descendant (identical result for leaf tenants).
        .listSandboxRuntimes({ limit: PAGE_SIZE, scope: 'subtree', ...(query ? { search: query } : {}) })
        .then((res) => {
          setRows(res.data);
          setHasMore(res.has_more);
          nextCursorRef.current = res.next_cursor;
          pagesLoadedRef.current = 1;
          loadedOnce.current = true;
          setStatus('success');
          setError(undefined);
        })
        .catch((err: unknown) => {
          setError(err instanceof Error ? err.message : 'Failed to load sandbox runtimes');
          if (!loadedOnce.current) setStatus('error');
        })
        .finally(() => {
          inFlight.current = false;
        });
    },
    [apiClient],
  );

  const fetchRef = useRef(fetchFirstPage);
  fetchRef.current = fetchFirstPage;
  const searchRef = useRef(debouncedSearch);
  searchRef.current = debouncedSearch;

  // Initial load + reload on search change.
  useEffect(() => {
    if (!enabled) return;
    setStatus((prev) => (prev === 'success' ? prev : 'loading'));
    fetchRef.current(debouncedSearch);
  }, [enabled, debouncedSearch]);

  // Liveness poll — first page only (see docstring).
  useEffect(() => {
    if (!enabled) return;
    const id = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      if (pagesLoadedRef.current !== 1) return;
      fetchRef.current(searchRef.current);
    }, pollInterval);
    return () => clearInterval(id);
  }, [enabled, pollInterval]);

  const loadMore = useCallback(() => {
    const cursor = nextCursorRef.current;
    if (!cursor || inFlight.current) return;
    inFlight.current = true;
    setLoadingMore(true);
    apiClient
      .listSandboxRuntimes({
        limit: PAGE_SIZE,
        scope: 'subtree',
        starting_after: cursor,
        ...(searchRef.current ? { search: searchRef.current } : {}),
      })
      .then((res) => {
        setRows((prev) => {
          const seen = new Set(prev.map((r) => r.pod_session_id));
          return [...prev, ...res.data.filter((r) => !seen.has(r.pod_session_id))];
        });
        setHasMore(res.has_more);
        nextCursorRef.current = res.next_cursor;
        pagesLoadedRef.current += 1;
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to load more sessions');
      })
      .finally(() => {
        inFlight.current = false;
        setLoadingMore(false);
      });
  }, [apiClient]);

  const reload = useCallback(() => fetchRef.current(searchRef.current), []);

  return {
    rows,
    status,
    error,
    /** True when a poll failed AFTER a successful first load — the data is
     * shown but momentarily stale; the next successful poll clears it. */
    stale: status === 'success' && !!error,
    search,
    setSearch,
    hasMore,
    loadingMore,
    loadMore,
    reload,
  };
}
